import type { Item } from './store';

export function parseIntoTable(input: string): string[][] {
  const rows = input.split('\n');
  return rows.map(row => row.split('\t').map(t => t.trim()));
}

export type ColumnStrategy = null | 'stockNumber' | 'description' | number; // number being quantity column

export type RowStrategy = 'ignore' | 'item' | 'serials';

export type ParsingStrategy = {
  columns: ColumnStrategy[];
  rows: RowStrategy[];
};

export function guessStrategy(lines: string[][]): ParsingStrategy {
  let strategy: ParsingStrategy = {
    columns: [],
    rows: [],
  };
  let qtyIndex = 0;
  let maybeStock = -1;

  const firstLine = lines[0]!;
  for (const column of firstLine) {
    const col = column.trim();
    if (!col) {
      maybeStock = strategy.columns.length;
      strategy.columns.push(null);
      continue;
    }
    const num = Number(col);
    if (isNaN(num)) {
      if (col.match(/^(\d|-)+$/)) {
        strategy.columns.push('stockNumber');
      } else {
        if (maybeStock >= 0) strategy.columns[maybeStock] = 'stockNumber';
        maybeStock = strategy.columns.length;
        strategy.columns.push('description');
      }
    } else {
      strategy.columns.push(qtyIndex);
      qtyIndex += 1;
    }
  }

  for (const line of lines) {
    const row = line.map(t => t.trim());
    if (row.every(t => !t)) {
      strategy.rows.push('ignore');
      continue;
    }
    let rowsWithData = 0;
    for (const col of row) {
      if (col) rowsWithData += 1;
    }
    if (rowsWithData === 1 && row.length > 1) {
      strategy.rows.push('serials');
    } else {
      strategy.rows.push('item');
    }
  }

  return strategy;
}

export function parseTableWithStrategy(
  table: string[][],
  strategy: ParsingStrategy,
): Item[] {
  const items: Item[] = [];

  for (let i = 0; i < table.length; i++) {
    const row = table[i]!;
    const item: Item = {
      stockNumber: '',
      description: '',
      quantities: new Array(10).fill(undefined),
    };
    const rowStrategy = strategy.rows[i]!;
    if (rowStrategy === 'ignore') continue;
    if (rowStrategy === 'serials') {
      const serials = row.flatMap(t =>
        t
          .trim()
          .split(/(,|\s)+/)
          .map(t => t?.trim())
          .filter(t => t),
      );
      if (items.length) {
        const lastItem = items[items.length - 1]!;
        lastItem.serials = lastItem.serials
          ? [...lastItem.serials, ...serials]
          : serials;
      }
      continue;
    }
    for (let j = 0; j < row.length; j++) {
      const column = row[j]!;
      const columnStrategy = strategy.columns[j]!;
      if (columnStrategy === null) continue;
      if (columnStrategy === 'stockNumber') {
        item.stockNumber = column;
      } else if (columnStrategy === 'description') {
        item.description = column;
      } else {
        const num = Number(column);
        item.quantities[columnStrategy] = isNaN(num) || !column ? null : num;
      }
    }
    items.push(item);
  }

  return items;
}
